import api from './api';
import { ProjectManifest, ArtifactoryNode, NodeStatus, HandoffPlugin } from '../types/artifactory';
import { useArtifactoryStore } from '../stores/artifactoryStore';

export const artifactoryService = {
  scaffoldProject(sessionId, intent, rootPath = "/", personaId = null) {
    return api.post(`/artifactory/scaffold`, {
      session_id: sessionId,
      intent,
      root_path: rootPath,
      persona_id: personaId
    }).then(response => response.data);
  },
  
  listArtifactoryProjects: async (): Promise<any[]> => {
    const response = await api.get('/artifactory/projects');
    // Ensure manifest_content is properly handled
    return response.data.map((project: any) => {
      if (project.manifest_content && typeof project.manifest_content === 'string') {
        try {
          project.manifest_content = JSON.parse(project.manifest_content);
        } catch (e) {
          console.warn('Failed to parse manifest_content as JSON', e);
        }
      }
      return project;
    });
  },

  getManifest: async (manifestId: string): Promise<ProjectManifest> => {
    const response = await api.get(`/artifactory/manifest/${manifestId}`);
    return response.data;
  },

  updateNode: async (manifestId: string, nodeId: string, updates: Partial<ArtifactoryNode>): Promise<ArtifactoryNode> => {
    const response = await api.patch(`/artifactory/manifest/${manifestId}/nodes/${nodeId}`, updates);
    return response.data;
  },

  hydrateNode: async (manifestId: string, nodeId: string, sessionId: string, withDependencies: boolean = false): Promise<any> => {
    // console.log(`Hydrating node ${nodeId} in manifest ${manifestId} ${withDependencies ? 'WITH' : 'WITHOUT'} dependencies`);
    // console.log(`withDependencies parameter type: ${typeof withDependencies}, value: ${withDependencies}`);
    
    // Ensure withDependencies is a boolean
    const boolWithDependencies = Boolean(withDependencies);
    
    const payload = {
      manifest_id: manifestId,
      session_id: sessionId,
      with_dependencies: boolWithDependencies
    };
    // console.log('Request payload:', payload);
    // console.log('JSON.stringify(payload):', JSON.stringify(payload));
    
    const response = await api.post(`/artifactory/hydrate/${nodeId}`, payload);
    // console.log(`Hydration response:`, response.data);
    return response.data;
  },

  hydrateNodeWithStatus: async (manifestId: string, nodeIdOrPath: string, sessionId: string, withDependencies: boolean = false): Promise<any> => {
    // First update local state to show hydrating status
    try {
      // Access the store using getState
      const store = useArtifactoryStore.getState();
      
      // Check if this is a path or a node ID
      const isPath = nodeIdOrPath.includes('/');
      
      // If it's a path, try to find the corresponding node ID
      let effectiveNodeId = nodeIdOrPath;
      
      if (isPath) {
        console.log(`Attempting to hydrate by path: ${nodeIdOrPath}`);
        
        // Try to find the node ID for this path in the store
        const manifest = store.manifest;
        if (manifest && manifest.nodes) {
          for (const [id, node] of Object.entries(manifest.nodes)) {
            const nodePath = node.path || '';
            // Compare with and without leading slash
            if (nodePath === nodeIdOrPath || 
                ('/' + nodePath) === nodeIdOrPath || 
                nodePath === ('/' + nodeIdOrPath)) {
              effectiveNodeId = id;
              console.log(`Resolved path ${nodeIdOrPath} to node ID ${id}`);
              break;
            }
          }
        }
        
        // Update status for both the path and the node ID if found
        store.updateNodeLocal(nodeIdOrPath, { status: "hydrating" });
        if (effectiveNodeId !== nodeIdOrPath) {
          store.updateNodeLocal(effectiveNodeId, { status: "hydrating" });
        }
      } else {
        // It's a node ID, update its status
        store.updateNodeLocal(nodeIdOrPath, { status: "hydrating" });
      }
      
      // Call the API to hydrate the node - always use the node ID endpoint if possible
      const result = await api.post(`/artifactory/hydrate/${effectiveNodeId}`, {
        manifest_id: manifestId,
        session_id: sessionId,
        with_dependencies: withDependencies
      });
      
      // Check if the response contains an error
      if (result.data.error) {
        throw new Error(result.data.error);
      }
      
      // Update the store with the result
      if (result.data.success && result.data.artifact_id) {
        // Update both the path and node ID if they're different
        store.updateNodeLocal(nodeIdOrPath, { 
          status: "fresh", 
          artifact_id: result.data.artifact_id 
        });
        
        if (effectiveNodeId !== nodeIdOrPath) {
          store.updateNodeLocal(effectiveNodeId, { 
            status: "fresh", 
            artifact_id: result.data.artifact_id 
          });
        }
      } else if (!result.data.success) {
        // If the operation wasn't successful but didn't throw an error
        throw new Error(result.data.message || "Hydration failed without specific error");
      }
      
      return result.data;
    } catch (error) {
      // Handle errors, including store access errors
      try {
        // Try to update the store if available
        const store = useArtifactoryStore.getState();
        store.updateNodeLocal(nodeIdOrPath, { status: "ghost" });
        
        // If we resolved to a different node ID, update that too
        if (effectiveNodeId !== nodeIdOrPath) {
          store.updateNodeLocal(effectiveNodeId, { status: "ghost" });
        }
        
        // Add error metadata to the node
        store.updateNodeLocal(nodeIdOrPath, { 
          metadata: { 
            last_error: error.message || "Unknown error occurred",
            last_error_timestamp: new Date().toISOString()
          } 
        });
      } catch (storeError) {
        console.error("Error accessing store:", storeError);
      }
      
      // Re-throw the original error
      throw error;
    }
  },

  fabricateAll: async (manifestId: string, sessionId: string): Promise<any> => {
    const response = await api.post(`/artifactory/fabricate/${manifestId}`, {
      session_id: sessionId
    });
    return response.data;
  },
  
  getSessionManifest: async (sessionId: string): Promise<{ has_manifest: boolean, manifest_id: string | null }> => {
    try {
      console.log(`Fetching manifest info for session: ${sessionId}`);
      const response = await api.get(`/artifactory/session/${sessionId}/manifest`);
      console.log(`Session manifest response:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching session manifest for ${sessionId}:`, error);
      return { has_manifest: false, manifest_id: null };
    }
  },
  
  mutateProject: async (manifestId: string, sessionId: string, intent: string): Promise<any> => {
    const response = await api.post(`/artifactory/mutate/${manifestId}`, {
      session_id: sessionId,
      intent: intent
    });
    return response.data;
  },

  getProjectHydrationStatus: async (manifestId: string): Promise<any> => {
    const response = await api.get(`/artifactory/hydration-status/${manifestId}`);
    return response.data;
  },

  getHydrationPlan: async (manifestId: string, focusNodeId?: string): Promise<any> => {
    try {
      console.log(`Requesting hydration plan for manifest ${manifestId}${focusNodeId ? ` with focus on node ${focusNodeId}` : ''}`);
      console.log(`getHydrationPlan - manifestId type: ${typeof manifestId}, value: ${manifestId}`);
      
      if (!manifestId) {
        console.error('Cannot request hydration plan: manifestId is empty or undefined');
        return { 
          error: "Missing manifest ID", 
          ordered_plan: [], 
          nodes_to_hydrate: 0 
        };
      }
      
      const url = focusNodeId 
        ? `/artifactory/hydration-plan/${manifestId}?focus_node_id=${focusNodeId}`
        : `/artifactory/hydration-plan/${manifestId}`;
      
      console.log(`Making API request to: ${url}`);
      const response = await api.get(url);
      console.log(`Hydration plan API response:`, response.data);
      
      // Ensure the response has the expected structure
      if (!response.data.ordered_plan) {
        console.warn('Hydration plan response missing ordered_plan:', response.data);
        // Add a default empty ordered_plan if missing
        return {
          ...response.data,
          ordered_plan: [],
          nodes_to_hydrate: 0
        };
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching hydration plan for manifest ${manifestId}:`, error);
      throw error;
    }
  },

  exportProject: (manifestId: string, includeStale: boolean = false, includeGhost: boolean = false): string => {
    return `/api/artifactory/export/${manifestId}?include_stale=${includeStale}&include_ghost=${includeGhost}`;
  },

  getHandoffPlugins: async (): Promise<HandoffPlugin[]> => {
    const response = await api.get('/artifactory/handoff/meta/plugins');
    return response.data;
  },

  handoffProject: (
    manifestId: string,
    tool: string,
    options: {
      includeStale?: boolean;
      includeGhost?: boolean;
      includeBuildOrder?: boolean;
      includeIntentHistory?: boolean;
    } = {}
  ): string => {
    const params = new URLSearchParams({
      tool,
      include_stale: String(options.includeStale ?? true),
      include_ghost: String(options.includeGhost ?? true),
      include_build_order: String(options.includeBuildOrder ?? true),
      include_intent_history: String(options.includeIntentHistory ?? false),
    });
    return `/api/artifactory/handoff/${manifestId}?${params.toString()}`;
  },
};
